import { datadogRum } from '@datadog/browser-rum';
import { useSession } from '@side/next-auth/react';
import { isBefore } from 'date-fns';
import { useEffect } from 'react';
import type { IdentityRequest } from '$api-clients/hooks';
import { useIdentity } from '$api-clients/hooks';
import { RESTRICTED_STATES_BY_FEATURE } from './constants';
import type { AppFeature } from './types';

// Matches the query the graph-api resolver issued so the SWR cache key (and the
// upstream request) is identical to the team-member fan-out it replaces.
const AGENT_TEAM_MEMBER_SEARCH =
  'status^("ACTIVE","ONBOARDING","OFFBOARDING") AND roles:"AGENT"';

// `interactions:read` grants global interaction access, standing in for the
// graph-api `globalInteractionAccess` LaunchDarkly flag the resolver checked
// first (that flag is not exposed to this client). See PLAT-10688.
const INTERACTIONS_READ_SCOPE = 'interactions:read';

type SubmitAsTeamMember = NonNullable<
  IdentityRequest<'/users/{userId}/interaction-permissions/{type}/team-members'>['Data']['data']
>[number];
type UserInfoTeam = NonNullable<
  IdentityRequest<'/auth/userinfo'>['Data']['teams']
>[number];
type IdentityLicense = SubmitAsTeamMember['license'];

const isLicenseExpired = (license: NonNullable<IdentityLicense>) => {
  if (!license.expiration) return true;

  return isBefore(new Date(license.expiration), new Date());
};

/**
 * Non-GraphQL replacement for the `hasAccessToFeature` graph resolver
 * (`graph-api/.../IdentityUser.ts` → `hasAccessToGeorestrictedFeature.ts`),
 * replicated in the client as part of moving identity-ui's `/user/*` pages off
 * GraphQL (PLAT-10688 / PLAT-10626). Ported from transactions-management-ui's
 * declarative `useHasAccessToFeature` (PR #3939).
 *
 * For a georestricted feature (currently only `LEASES`, restricted in
 * `{ OR, WA }`) the user has access if any of:
 *   1. they hold the `interactions:read` scope (global interaction access), or
 *   2. their roles include `ADMIN` or `AUDITOR`, or
 *   3. any associated submit-as AGENT (or one of the user's own accessible
 *      teams) has a non-expired license/market in a non-restricted state.
 *
 * Both reads go through `useIdentity` (SWR) so every consumer shares one cache
 * entry per key and re-renders/remounts are served from cache — the license
 * lookups only run when the georestricted path is actually reached. Returns
 * `undefined` while a required upstream read is still loading so the nav link
 * doesn't flash. Any non-georestricted/unknown feature returns `false`, matching
 * the resolver's `default: false` branch.
 */
export function useHasAccessToFeature(
  feature: AppFeature,
): boolean | undefined {
  const session = useSession();
  const interactionsReadLoading = session.status === 'loading';
  const hasInteractionsReadPermission = Boolean(
    session.data?.hasScopes?.([INTERACTIONS_READ_SCOPE]),
  );

  // Read synchronously for the decision — NOT as an effect dependency, so an
  // unstable `user` reference can no longer trigger refetches.
  const user = session.data?.user;
  const userId = user?.id;
  const roles = user?.roles ?? [];

  const restrictedStates =
    RESTRICTED_STATES_BY_FEATURE[
      feature as keyof typeof RESTRICTED_STATES_BY_FEATURE
    ];

  const directAccess =
    roles.includes('ADMIN') ||
    roles.includes('AUDITOR') ||
    hasInteractionsReadPermission;

  // Only the georestricted path needs the license lookups. Admins/auditors and
  // non-georestricted features short-circuit before any network call — so most
  // renders fetch nothing here.
  const needsLicenseCheck =
    Boolean(userId) && Boolean(restrictedStates) && !directAccess;

  const {
    data: userInfo,
    isLoading: userInfoLoading,
    error: userInfoError,
  } = useIdentity('/auth/userinfo', needsLicenseCheck ? undefined : null);

  const {
    data: submitAsResponse,
    isLoading: submitAsLoading,
    error: submitAsError,
  } = useIdentity(
    '/users/{userId}/interaction-permissions/{type}/team-members',
    needsLicenseCheck && userId
      ? {
          params: {
            path: { userId, type: 'submit-as' },
            query: { limit: 500, search: AGENT_TEAM_MEMBER_SEARCH },
          },
        }
      : null,
  );

  // Without this, an API outage during the georestriction check is
  // indistinguishable from "user has no access" — the hook returns `false`
  // either way (data is undefined). Reporting the error preserves the
  // observability the old graph-backed implementation had.
  useEffect(() => {
    const error = userInfoError ?? submitAsError;
    if (error !== undefined) {
      datadogRum.addError(error, {
        userId,
        message: 'Error determining geolocation restricted feature access',
      });
    }
  }, [userInfoError, submitAsError, userId]);

  if (interactionsReadLoading) return undefined;
  if (!userId) return undefined;

  // Non-georestricted / unknown feature — parity with the resolver's
  // `default: false` branch.
  if (!restrictedStates) return false;
  if (directAccess) return true;

  if (userInfoLoading || submitAsLoading) return undefined;

  const activeTeamIds = new Set(
    Object.keys(userInfo?.accessibleTeams?.nodes ?? {}),
  );
  const currentUserTeams: UserInfoTeam[] = userInfo?.teams ?? [];
  const currentUserActiveTeamMarkets = currentUserTeams
    .filter((team) => activeTeamIds.has(team.id))
    .map((team) => team.market);

  const submitAsTeamMembers: SubmitAsTeamMember[] =
    submitAsResponse?.data ?? [];

  return (
    submitAsTeamMembers.some(
      ({ license }) =>
        license &&
        !isLicenseExpired(license) &&
        !restrictedStates.has(license.state),
    ) ||
    currentUserActiveTeamMarkets.some(
      (market) => market && !restrictedStates.has(market),
    )
  );
}
